import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Spin, message } from "antd";
import dayjs from "dayjs";
import {
  SaveJobDocuments,
  SaveJobHistory,
  fetchAppliedJobs,
  saveAcQualifications,
  saveJobApplication,
} from "../../features/job/jobSlice";

export default function JobApplyDocs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    profileData,
    experiences = [],
    educations = [],
    qualifications = [],
    certificates = [],
  } = useSelector((state) => state.profile);

  const { applicationData, mandatoryDocs = [], jobTitle } = location.state || {};

  const [expectedSalary, setExpectedSalary] = useState("");
  const [currentSalary, setCurrentSalary] = useState("");
  const [fileMap, setFileMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  if (!applicationData) {
    navigate("/job-vacancies");
    return null;
  }

  const handleFileChange = (lineNo, e) => {
    const file = e.target.files[0];
    if (file) setFileMap((prev) => ({ ...prev, [lineNo]: file }));
  };

  const submitApplication = async () => {
    setIsLoading(true);

    const finalData = {
      ...applicationData,
      expectedSalary: Number(expectedSalary) || 1,
      postalAddress: currentSalary,
    };

    const res = await dispatch(saveJobApplication(finalData));
    if (res.payload.success === false) {
      message.error("Application Failed");
      setIsLoading(false);
      return;
    }

    const jobAppNo = res.payload.messages.message;

    experiences.forEach((exp) => {
      const endDate = exp.isCurrentJob ? dayjs() : dayjs(exp.endDate);
      const startDate = dayjs(exp.startDate);
      dispatch(SaveJobHistory({
        jobApplicationNo: jobAppNo,
        organization: exp.employerName,
        organizationAddress: exp.employerName,
        positionHeld: exp.jobTitle,
        fromDate: startDate.format("YYYY-MM-DD"),
        toDate: endDate.format("YYYY-MM-DD"),
        yearsOfService: endDate.diff(startDate, "year"),
        grossSalary: "0",
        benefits: "none",
        email: "null",
        jobDescription: exp.workDescription || "",
      }));
    });

    educations.forEach((edu) => {
      dispatch(saveAcQualifications({
        jobApplicationNo: jobAppNo,
        qualificationCode: edu.qualification,
        qualificationName: edu.qualification,
        joiningDate: dayjs(edu.startDate).format("YYYY-MM-DD"),
        completionDate: dayjs(edu.endDate).format("YYYY-MM-DD"),
        instutitionName: edu.institutionName,
        professionQualification: false,
        yearOfRegistration: dayjs(edu.endDate).format("YYYY"),
        award: "0",
        awardDate: "2023-01-02",
        email: "",
      }));
    });

    qualifications.forEach((qual) => {
      dispatch(saveAcQualifications({
        jobApplicationNo: jobAppNo,
        qualificationCode: qual.qualification,
        qualificationName: qual.qualification,
        joiningDate: "2023-01-02",
        completionDate: "2023-01-02",
        instutitionName: qual.institutionName,
        professionQualification: true,
        award: "0",
        awardDate: "2023-01-02",
        email: "",
        yearOfRegistration: String(qual.yearOfQualification),
      }));
    });

    for (const doc of mandatoryDocs) {
      if (fileMap[doc.lineNo]) {
        await dispatch(SaveJobDocuments({
          jobApplicationNo: jobAppNo,
          documentDescription: doc.descripiton,
          actualFileName: fileMap[doc.lineNo].name,
        }));
      }
    }

    certificates.forEach((cert) => {
      dispatch(SaveJobDocuments({
        jobApplicationNo: jobAppNo,
        documentDescription: `Cert Number: ${cert.certificateNumber || ""}`,
        actualFileName: cert.certificatePath || "",
      }));
    });

    message.success("Application submitted successfully");
    dispatch(fetchAppliedJobs(profileData?.nationalId));
    setIsLoading(false);
    navigate("/");
  };

  const handleSubmit = () => {
    if (!expectedSalary) {
      message.error("Please enter your expected salary");
      return;
    }
    if (!currentSalary) {
      message.error("Please enter your current salary");
      return;
    }
    const missingDocs = mandatoryDocs.filter((doc) => !fileMap[doc.lineNo]);
    if (missingDocs.length) {
      message.error(`Please upload: ${missingDocs.map((d) => d.descripiton).join(", ")}`);
      return;
    }

    Modal.confirm({
      title: "Confirm Application",
      content: `Are you sure you want to submit your application for ${jobTitle}?`,
      okText: "Yes, Submit",
      cancelText: "Cancel",
      okButtonProps: { style: { backgroundColor: "#147CBC", borderColor: "#147CBC" } },
      onOk: submitApplication,
    });
  };

  return (
    <div className="m-[10px] md:mr-[108px] md:ml-[42px] md:mt-[15px]">
      <div className="bg-white p-4 md:p-[53px]">
        <h2 className="prof-name mb-1">
          Complete Your <span className="text-[#147CBC]">Application</span>
        </h2>
        <p className="prof-desc mb-8">
          Applying for: <strong>{jobTitle}</strong>
        </p>

        {/* Disclaimer */}
        <div className="flex gap-3 border border-amber-300 bg-amber-50 rounded-[8px] p-4 mb-8">
          <span className="text-amber-500 text-[20px] mt-0.5">⚠</span>
          <div>
            <p className="font-semibold text-amber-800 text-[14px] mb-1">
              Declaration of Accuracy
            </p>
            <p className="text-amber-700 text-[13px] leading-relaxed">
              By submitting this application, you confirm that all information provided is
              accurate, complete, and truthful to the best of your knowledge. Submission of
              false, misleading, or fraudulent information — including misrepresented
              qualifications, falsified documents, or inaccurate employment history — may
              result in the immediate disqualification of your application and a permanent ban
              from future opportunities with this organisation.
            </p>
          </div>
        </div>

        {/* Salary fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-[15px] text-[#141414]">
              Expected Salary (KES) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              value={expectedSalary}
              onChange={(e) => setExpectedSalary(e.target.value)}
              placeholder="e.g. 80000"
              className="border border-gray-300 rounded-[8px] px-4 py-3 text-[15px] focus:outline-none focus:border-[#147CBC]"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-[15px] text-[#141414]">
              Current Salary (KES) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              value={currentSalary}
              onChange={(e) => setCurrentSalary(e.target.value)}
              placeholder="e.g. 60000"
              className="border border-gray-300 rounded-[8px] px-4 py-3 text-[15px] focus:outline-none focus:border-[#147CBC]"
            />
          </div>
        </div>

        {/* Mandatory documents */}
        {mandatoryDocs.length > 0 && (
          <>
            <h3 className="font-semibold text-[16px] text-[#141414] mb-4">
              Required Documents
            </h3>
            <div className="flex flex-col gap-6 mb-8">
              {mandatoryDocs.map((doc) => (
                <div key={doc.lineNo} className="border border-gray-200 rounded-[8px] p-5">
                  <p className="font-semibold text-[15px] text-[#141414] mb-3">
                    {doc.descripiton}
                    <span className="ml-2 text-xs text-red-500 font-normal">* Required</span>
                  </p>
                  <label className="flex items-center gap-3 cursor-pointer w-fit">
                    <span className="border-2 border-[#147CBC] text-[#147CBC] rounded-[35px] px-6 py-2 text-[14px] font-semibold hover:bg-[#f0f7ff] transition-all">
                      {fileMap[doc.lineNo] ? "Change File" : "Select File"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => handleFileChange(doc.lineNo, e)}
                    />
                  </label>
                  {fileMap[doc.lineNo] && (
                    <p className="mt-2 text-sm text-green-600">✓ {fileMap[doc.lineNo].name}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex gap-4 mt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="border-2 border-[#147CBC] text-[#147CBC] bg-white rounded-[35px] px-8 py-3 font-semibold text-[16px] hover:bg-[#f0f7ff] transition-all"
          >
            Go Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-[#147CBC] rounded-[35px] text-white px-8 py-3 font-semibold text-[16px] hover:bg-[#0e5a8a] transition-all disabled:opacity-60"
          >
            {isLoading ? <Spin size="small" /> : "Submit Application"}
          </button>
        </div>
      </div>
    </div>
  );
}
